import { useRef, useEffect } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ isAgent }) => ({
  display: 'inline-block',
  maxWidth: '80%',
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: isAgent ? '#FFE5E5' : '#E5F0FF',
  color: '#333',
  ...(isAgent
    ? { borderTopLeftRadius: 0 }
    : { borderTopRightRadius: 0, marginLeft: 'auto' }),
}));

const TimeStamp = styled(Typography)({
  fontSize: '0.7rem',
  color: 'rgba(0, 0, 0, 0.54)',
  marginTop: 2,
});

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ isAgent }) => ({
  marginBottom: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: isAgent ? 'flex-start' : 'flex-end',
}));

const TypingIndicator = styled(motion.div)(() => ({
  display: 'flex',
  gap: 6,
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: '#FFE5E5',
  borderTopLeftRadius: 0,
}));

const TypingDot = styled(motion.div)(() => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#E84749',
}));

interface ConversationAreaProps {
  transcript: Array<{
    source: string;
    message: string;
    timestamp: Date;
  }>;
  isSpeaking: boolean;
  company?: string;
}

// Header text by company
const HEADER_TEXT: Record<string, { title: string; subtitle: string }> = {
  coupang: {
    title: 'Coupang人事サポート',
    subtitle: '給与・労務相談窓口',
  },
  grab: {
    title: 'Grab Training Support',
    subtitle: 'Product Orientation',
  },
};

export function HRConversationArea({
  transcript,
  isSpeaking,
  company = 'coupang',
}: ConversationAreaProps) {
  const headerText = HEADER_TEXT[company] || HEADER_TEXT.coupang;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, isSpeaking]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography variant="body2" color="error" fontWeight={600}>
          {headerText.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {headerText.subtitle}
        </Typography>
      </Box>

      <Divider />

      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
        }}
      >
        {transcript.map((item, index) => (
          <MessageContainer key={index} isAgent={item.source === 'ai'}>
            <MessageBubble isAgent={item.source === 'ai'} elevation={0}>
              {item.message}
            </MessageBubble>
            <TimeStamp>
              {item.timestamp.toLocaleTimeString('ja-JP', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TimeStamp>
          </MessageContainer>
        ))}

        {isSpeaking && (
          <MessageContainer isAgent={true}>
            <TypingIndicator>
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
              />
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
              />
            </TypingIndicator>
          </MessageContainer>
        )}
      </Box>
    </Box>
  );
}